import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UseFormSetError } from 'react-hook-form';
import { useCreateUserMutation } from '../hooks/useCreateUserMutation';
import { UserForm } from '../components/UserForm';
import { handleUserMutationError } from '../services/userErrorHandler';
import { type CreateUserFormInputs } from '../schemas/user.schema';
import { assignSecurityGroupsToUser } from '@/features/security-admin/services/security-groups';
import type { AssignUserToGroupsPayload } from '@/features/security-admin/types/security-admin.types';
import { assignUserRole } from '../services/userRbac';
import { Alert, TextLink } from '@/shared/components';

export function UserCreate() {
    const navigate = useNavigate();
    const mutation = useCreateUserMutation();
    const [error, setError] = useState<string | null>(null);
    const [isAttaching, setIsAttaching] = useState(false);
    const [assignFailure, setAssignFailure] = useState<{ userId: string; groupCount: number } | null>(null);
    const [roleAssignFailure, setRoleAssignFailure] = useState<
        { userId: string; failedCount: number; totalCount: number } | null
    >(null);
    const formSetErrorRef = useRef<UseFormSetError<CreateUserFormInputs> | null>(null);
    const groupSelectionGetterRef = useRef<(() => AssignUserToGroupsPayload | null) | null>(null);
    const roleSelectionGetterRef = useRef<(() => string[]) | null>(null);

    const handleSubmit = (data: CreateUserFormInputs) => {
        setError(null);
        setAssignFailure(null);
        setRoleAssignFailure(null);
        mutation.mutate(data, {
            onSuccess: async (user) => {
                setIsAttaching(true);
                try {
                    const selection = groupSelectionGetterRef.current?.() ?? null;
                    const roleIds = roleSelectionGetterRef.current?.() ?? [];

                    const groupPromise = selection && selection.group_ids.length > 0
                        ? assignSecurityGroupsToUser(user.id, selection).then(() => true).catch(() => false)
                        : Promise.resolve(true);

                    const rolePromise = roleIds.length > 0
                        ? Promise.allSettled(roleIds.map((roleId) => assignUserRole(user.id, roleId)))
                        : Promise.resolve([]);

                    const [groupsOk, roleResults] = await Promise.all([groupPromise, rolePromise]);

                    if (!groupsOk) {
                        setAssignFailure({ userId: user.id, groupCount: selection!.group_ids.length });
                    }

                    const failedRoleCount = roleResults.filter((r) => r.status === 'rejected').length;
                    if (failedRoleCount > 0) {
                        setRoleAssignFailure({ userId: user.id, failedCount: failedRoleCount, totalCount: roleIds.length });
                    }

                    if (groupsOk && failedRoleCount === 0) {
                        navigate(`/admin/users/${user.id}`);
                    }
                } finally {
                    setIsAttaching(false);
                }
            },
            onError: (err) => {
                const result = handleUserMutationError(err);
                if (result.status === 422 && result.fieldErrors && formSetErrorRef.current) {
                    for (const [field, messages] of Object.entries(result.fieldErrors)) {
                        formSetErrorRef.current(
                            field as Parameters<UseFormSetError<CreateUserFormInputs>>[0],
                            { type: 'server', message: messages[0] ?? 'Invalid' },
                        );
                    }
                    return;
                }
                if (!result.handled) {
                    setError(err.message || 'Failed to create user');
                }
            },
        });
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to="/admin/users" color="secondary" className="text-sm">
                    &larr; Back to Users
                </TextLink>
            </div>

            <h1 className="text-2xl font-semibold">Create User</h1>

            {error && (
                <Alert variant="error">
                    <span>{error}</span>
                </Alert>
            )}

            {assignFailure && (
                <Alert variant="error">
                    <span>
                        The user was created, but adding them to the selected security{' '}
                        {assignFailure.groupCount === 1 ? 'group' : 'groups'} failed.{' '}
                        <TextLink to={`/admin/users/${assignFailure.userId}`} color="inherit">
                            View user
                        </TextLink>{' '}
                        to retry from their profile.
                    </span>
                </Alert>
            )}

            {roleAssignFailure && (
                <Alert variant="error">
                    <span>
                        The user was created, but assigning {roleAssignFailure.failedCount} of{' '}
                        {roleAssignFailure.totalCount} selected{' '}
                        {roleAssignFailure.totalCount === 1 ? 'role' : 'roles'} failed.{' '}
                        <TextLink to={`/admin/users/${roleAssignFailure.userId}`} color="inherit">
                            View user
                        </TextLink>{' '}
                        to retry from their Roles tab.
                    </span>
                </Alert>
            )}

            {/* React Query does not await the per-call onSuccess, so isPending
                clears before the attach fan-out settles. */}
            <UserForm
                onSubmit={handleSubmit}
                isLoading={mutation.isPending || isAttaching}
                submitLabel="Create User"
                onSetErrorRef={(s) => { formSetErrorRef.current = s; }}
                onSecurityGroupSelectionRef={(getter) => { groupSelectionGetterRef.current = getter; }}
                onRoleSelectionRef={(getter) => { roleSelectionGetterRef.current = getter; }}
            />
        </section>
    );
}
